import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { jwtDecode } from "jwt-decode";

import { CustomLoginService } from '@privatelib/ngx18-authz-dev';
import { BehaviorSubject, Observable, catchError, combineLatest, map, of, switchMap } from 'rxjs';
import { ROUTE_CONSTANTS, API_ROUTES } from 'app/util/route.constants';
import { PreferencesService } from '../preferences/preferences.service';
import { UserManagementService } from '../user-management/user-management.service';
import { UserDetails } from 'app/util/constants';
import { SessionInactivityService } from '../session-inactivity/session-inactivity.service';
import { CVANotificationService } from '../cva-notification/cva-notification.service';
import { NotificationClassEnum } from 'app/models/notification-class.enum';
import { environment } from '../../../environments/environment';

export interface PractitionerPayload {
  providerId: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  phoneNo?: string;
  company?: string;
  id: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  tokenKey = 'idToken';
  loggedIn$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  userSubject: BehaviorSubject<PractitionerPayload | null> = new BehaviorSubject<PractitionerPayload | null>(null);
  currentUser$ = this.userSubject.asObservable();
  notificationShown = false;
  reauthenticationRequired = false;

  constructor(
    private readonly http: HttpClient,
    private readonly loginService: CustomLoginService,
    private readonly router: Router,
    private readonly preferencesService: PreferencesService,
    private readonly userManagementService: UserManagementService,
    private readonly sessionInactivityService: SessionInactivityService,
    private readonly cvaNotificationService: CVANotificationService
  ) { }

  isLoggedIn(): Observable<boolean> {
    return combineLatest([
      this.loginService.verifyIdToken().pipe(
        catchError((error) => {
          const status = error?.status ?? 0;

          // Workaround flag to avoid repeated notifications for the same error
          if (status === 423 && !this.notificationShown) {
            this.notificationShown = true;
            this.cvaNotificationService.showNotification(error.message, NotificationClassEnum.ERROR)
          }
          return of({ status });
        })
      ),
      this.loginService.verifyAuthorizationToken().pipe(catchError(() => of({ status: 0 })))
    ]).pipe(
      // eslint-disable-next-line  @typescript-eslint/no-explicit-any
      map(([idTokenResponse, authTokenResponse]: any[]) => {
        return idTokenResponse?.status === 200 && authTokenResponse?.status === 200;
      }),
      // Only fetch user details if token verification succeeded
      switchMap((tokensValid) => {
        const actionMap = {
          "true": () => this.saveUserDetails(),
          "false": () => of(false),
        };
        return actionMap[String(tokensValid)]?.();
      })
    );
  }

  logout(inDirectSignout = true) {
    this.loginService.logout().subscribe({
      next: () => {
        this.revokeOnLogout();
        if (inDirectSignout) {
          this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
        }
      },
      error: (err: Error) => {
        console.error(err)
        this.revokeOnLogout();
      }
    })
  }

  onPartialLogout() {
    this.reauthenticationRequired = true;
    this.loginService.logout(false).subscribe({
      next: () => {
        localStorage.removeItem("idToken");
        localStorage.removeItem("authToken");
        this.loggedIn$.next(false);
      },
      error: (err: Error) => {
        console.error(err)
      }
    })
  }

  // Decode the JWT token and extract user information
  decodeToken(token: string): PractitionerPayload {
    return jwtDecode<PractitionerPayload>(token);
  }

  /**
   * Checks the user details and refreshes if needed.
   * 
   * @param isToBeRefresh Whether the user details should be refreshed.
   * @returns Observable<boolean> Whether the user is logged in or not.
   */
  saveUserDetails(isToBeRefresh = false): Observable<boolean> {
    const token = localStorage.getItem(this.tokenKey);

    // If no token exists, return false immediately
    if (!token) {
      return of(false);
    }

    // Decode the token
    const decodedToken = this.decodeToken(token);
    if (!decodedToken) {
      return of(false);
    }

    // Check if user data should be refreshed or if it is not cached
    if (this.userSubject.value && !isToBeRefresh) {
      return of(true); // Return true if user data is already cached and no refresh is needed
    }

    // Fetch user details from API
    return this.userManagementService.getUserDetails(decodedToken.id).pipe(
      map((res: { success: boolean; data: UserDetails }) => {
        if (res.success && res.data?.id) {

          // Update the userSubject with the new user details
          this.userSubject.next({
            firstName: res.data.firstName,
            lastName: res.data.lastName,
            providerId: res.data.providerId,
            email: res.data.email,
            phoneNo: res.data?.phoneNo,
            company: res.data?.company,
            id: decodedToken.id,

          });
          return true;
        }
        return false;
      }),
      catchError((error) => {
        console.log("Error on pulling user details:", error);
        return of(false);
      })
    );
  }

  revokeOnLogout() {
    this.reauthenticationRequired = false;
    this.userSubject.next(null);
    this.loggedIn$.next(false);
    this.preferencesService.clearPreferences();
    localStorage.clear();
    sessionStorage.clear();
    this.sessionInactivityService.stopOnLogout();
    this.notificationShown = false;
  }

  /**
   * Validate OTP for two-step verification
   * @param email - User email
   * @param otp - One-time password
   * @returns Observable with OTP validation response
   */
  validateLoginOtp(email: string, otp: string): Observable<{redirectUrl: string}> {
    return this.http.post<{redirectUrl: string}>(`${environment.user_management_api_url}${API_ROUTES.VALIDATE_LOGIN_OTP}`, {
      email,
      otp
    });
  }

  redirectToResetPassword(redirectUrl: string) {
    redirectUrl += `&reason=expired`;
    const currentOrigin = window.location.origin;

    try {
      const url = new URL(redirectUrl);
      if (url.origin === currentOrigin) {
        // Internal route
        const relativePath = url.pathname + url.search + url.hash;
        this.navigateByUrl(relativePath);
      } else {
        // External URL
        this.goTo(redirectUrl);
      }
    } catch (err) {
      console.error('Invalid redirect URL:', err);
    }
  }

  goTo(url: string) {
    window.location.href = url;
  }

  navigateByUrl(redirectUrl: string) {
    this.router.navigateByUrl(redirectUrl);
  }
}
